import { computed, onMounted, Ref, watch } from "vue";
import { useStore } from "vuex";
import provideHook from "@/lib/provideHook";

import { fullName } from "@/mixins";
import { AppointmentServiceCategory } from "@/typings/appointment";
import { Technician } from "@/typings/technician";

export const useAppointmentSchedulingFields = provideHook(
  (
    formData: Ref<{
      service_category_id: number | null;
      technician_id: number | number[] | null;
    }>,
    allTechs: Ref<boolean>
  ) => {
    const store = useStore();

    onMounted(() => {
      fetchServiceCategories();
      fetchTechnicians();
    });

    const fetchServiceCategories = () => {
      if (store.state.serviceCategories.length === 0) {
        store.dispatch("fetchServiceCategories");
      }
    };

    const serviceCategoryOptions = computed(() =>
      store.state.serviceCategories
        .filter((sc: AppointmentServiceCategory) => !sc.archived)
        .map((sc: { id: number; name: string }) => ({
          label: sc.name,
          value: sc.id
        }))
    );

    const fetchTechnicians = () => {
      if (store.state.technicians.length === 0) {
        store.dispatch("fetchTechnicians");
      }
    };

    const technicianOptions = computed(() => {
      let technicians: Technician[] = store.state.technicians;

      // If filtering by service category and "Include all technicians" is not checked
      if (!allTechs.value && formData.value.service_category_id) {
        technicians = technicians.filter((tech: Technician) =>
          tech.service_categories_ids.includes(
            formData.value.service_category_id
          )
        );
      }

      return technicians.map((tech) => ({
        ...tech,
        label: fullName(tech),
        value: tech.user_id
      }));
    });

    // Remove selected technicians that are no longer in the filtered options
    watch(technicianOptions, (options) => {
      const id = formData.value.technician_id;
      if (id == null) return;

      const validIds = new Set(options.map((opt) => opt.value));

      if (Array.isArray(id)) {
        const filtered = id.filter((v) => validIds.has(v));
        if (filtered.length !== id.length) {
          formData.value.technician_id = filtered.length ? filtered : [];
        }
      } else if (!validIds.has(id)) {
        formData.value.technician_id = null;
      }
    });

    const selectedServiceCategory = computed<
      AppointmentServiceCategory | undefined
    >(() => {
      if (!formData.value.service_category_id) return undefined;
      return store.state.serviceCategories.find(
        (sc: AppointmentServiceCategory) =>
          sc.id === formData.value.service_category_id
      );
    });
    const selectedTech = computed<Technician | undefined>(() => {
      const id = formData.value.technician_id;
      if (!id) return undefined;
      const primaryId = Array.isArray(id) ? id[0] : id;
      if (!primaryId) return undefined;
      return store.state.technicians.find(
        (tech: Technician) => tech.user_id === primaryId
      );
    });

    return {
      fetchServiceCategories,
      serviceCategoryOptions,
      fetchTechnicians,
      technicianOptions,
      selectedServiceCategory,
      selectedTech
    };
  }
);
